import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toEmbedUrl } from "@/lib/video";
import { isUploaded } from "@/lib/videoStream";
import LazyVideo from "@/components/LazyVideo";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Wrench, Search, AlertOctagon, ArrowLeft, PlayCircle, ListTree, Info } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Classification from "@/components/Classification";

const SEARCH_FIELDS = ["product_name", "model", "sku", "product_category", "problem", "symptoms"];
const EXAMPLES = ["remote not pairing", "adjustable base not moving", "massage not working", "mattress smell", "bed frame noise", "USB not working"];

function Field({ label, children }) {
  if (!children) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-line">{children}</div>
    </div>
  );
}

function Article({ a, onBack, videos = [] }) {
  const linked = videos.filter((v) => {
    const la = Array.isArray(v.linked_articles) ? v.linked_articles : (v.linked_articles || "").split(",");
    return la.map((x) => x.trim()).includes(a.id) && (isUploaded(v) || v.video_url);
  });
  return (
    <div className="space-y-5 max-w-3xl" data-testid="article-view">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-mlily-blue" data-testid="article-back">
        <ArrowLeft className="w-4 h-4" /> Back to results
      </button>
      <div className="flex flex-wrap gap-2 items-center">
        <Classification value={a.classification} />
        <Badge className="bg-amber-500">{a.product_category}</Badge>
        {a.model && <Badge variant="outline">{a.model}</Badge>}
        {a.status && <Badge variant="outline">{a.status}</Badge>}
        {a.needs_review && <Badge className="bg-red-500">Flagged for Admin review</Badge>}
      </div>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{a.problem}</h1>

      <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-mlily-blue" data-testid="model-warning">
        <Info className="w-4 h-4 mt-0.5 shrink-0" /> Remote layouts and pairing procedures may vary by model. Always confirm the model before providing model-specific instructions.
      </div>

      {a.safety_warning && (
        <div className="rounded-xl border-2 border-red-500 bg-red-50 p-4" data-testid="article-safety">
          <div className="flex items-center gap-2 text-red-700 font-heading font-bold">
            <AlertOctagon className="w-5 h-5" /> Safety Warning
          </div>
          <p className="text-sm text-red-800 mt-1">{a.safety_warning}</p>
        </div>
      )}

      <Card className="p-6 grid sm:grid-cols-2 gap-4">
        <Field label="Product / Type">{a.product_name}</Field>
        <Field label="SKU">{a.sku}</Field>
        <Field label="Symptoms">{a.symptoms}</Field>
        <Field label="Possible Fault">{a.possible_cause}</Field>
        <Field label="Before You Start">{a.before_you_start}</Field>
        <Field label="Expected / Normal Result">{a.expected_result}</Field>
        <Field label="Photos Required">{a.photos_required}</Field>
        <Field label="Video Required">{a.video_required}</Field>
        <Field label="Escalation Point">{a.escalation_point}</Field>
      </Card>

      {a.questions_to_ask?.length > 0 && (
        <Card className="p-6" data-testid="article-questions">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Questions to ask the customer</h3>
          <ul className="space-y-2 list-disc pl-5">
            {a.questions_to_ask.map((qn, i) => (
              <li key={i} className="text-sm text-slate-700">{qn}</li>
            ))}
          </ul>
        </Card>
      )}

      {a.steps?.length > 0 && (
        <Card className="p-6">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Step-by-step troubleshooting</h3>
          <ol className="space-y-3">
            {a.steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-mlily-blue text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-sm text-slate-700">{s}</span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {a.video_url && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-slate-900 font-semibold"><PlayCircle className="w-5 h-5 text-violet-500" /> Approved video</div>
            <Badge className={a.video_customer_safe === "Yes" ? "bg-emerald-500" : "bg-slate-500"}>
              {a.video_customer_safe === "Yes" ? "Customer-safe" : "Internal only"}
            </Badge>
          </div>
          <div className="aspect-video rounded-lg overflow-hidden bg-black">
            <iframe title="video" src={toEmbedUrl(a.video_url)} className="w-full h-full" allowFullScreen />
          </div>
        </Card>
      )}

      {linked.length > 0 && (
        <div className="space-y-3" data-testid="article-linked-videos">
          <h3 className="font-heading font-bold text-slate-900">Related videos</h3>
          {linked.map((v) => (
            <Card key={v.id} className="p-4" data-testid={`article-video-${v.id}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-slate-900 font-semibold"><PlayCircle className="w-5 h-5 text-violet-500" /> {v.title}</div>
                <div className="flex gap-2">
                  <Classification value={v.classification} />
                  <Badge className={v.customer_safe === "Yes" ? "bg-emerald-500" : "bg-slate-500"}>
                    {v.customer_safe === "Yes" ? "Customer-safe" : "Internal only"}
                  </Badge>
                </div>
              </div>
              <div className="aspect-video rounded-lg overflow-hidden bg-black">
                {isUploaded(v)
                  ? <LazyVideo vid={v.id} testId={`ts-video-player-${v.id}`} />
                  : <iframe title={v.title} src={toEmbedUrl(v.video_url)} className="w-full h-full" allowFullScreen />}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-6 grid sm:grid-cols-2 gap-4">
        <Field label="Resolution">{a.resolution}</Field>
        <Field label="What to Tell Customer">{a.tell_customer}</Field>
        <Field label="Replacement Part Required">{a.spare_part}</Field>
        <Field label="Spare Part Code">{a.spare_part_code}</Field>
        <Field label="Approval Required">{a.approval_required}</Field>
        <Field label="Technician Required">{a.technician_required}</Field>
        <Field label="Source">{a.source}</Field>
        <Field label="Last Reviewed">{a.last_reviewed}</Field>
        <Field label="Version">{a.version}</Field>
      </Card>

      {a.internal_notes && (
        <Card className="p-6 border-l-4 border-mlily-blue bg-blue-50/40" data-testid="article-internal-notes">
          <div className="flex items-center gap-2 text-mlily-blue font-heading font-bold mb-2">
            <Info className="w-5 h-5" /> Internal Staff Notes
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-line">{a.internal_notes}</p>
        </Card>
      )}
    </div>
  );
}

export default function Troubleshooting() {
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [flows, setFlows] = useState([]);
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/content/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/content/flows").then((r) => setFlows(r.data)).catch(() => {});
    api.get("/content/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setQ(params.get("q") || ""); }, [params]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return articles;
    return articles.filter((a) => SEARCH_FIELDS.some((f) => (a[f] || "").toString().toLowerCase().includes(term)));
  }, [q, articles]);

  if (selected) return <Article a={selected} videos={videos} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-6" data-testid="troubleshooting-page">
      <PageHeader overline="Troubleshooting" title="Troubleshoot a Product" icon={Wrench}
        color="text-amber-600 bg-amber-50"
        desc="Search by product, model, SKU, category or the customer's reported problem." />

      <div className="relative max-w-2xl">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="troubleshooting-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setParams(e.target.value ? { q: e.target.value } : {}); }}
          placeholder="e.g. remote not pairing, mattress smell, USB not working"
          className="w-full pl-12 pr-4 h-13 py-3.5 rounded-xl border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {EXAMPLES.map((e) => (
          <button key={e} onClick={() => { setQ(e); setParams({ q: e }); }}
            data-testid={`example-${e.replace(/\s+/g, "-")}`}
            className="text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
            {e}
          </button>
        ))}
      </div>

      {/* Guided flows entry */}
      {flows.length > 0 && (
        <Card className="p-5 border-mlily-blue/30 bg-blue-50/40">
          <div className="flex items-center gap-2 mb-3 font-heading font-bold text-slate-900">
            <ListTree className="w-5 h-5 text-mlily-blue" /> Interactive guided troubleshooting
          </div>
          <div className="flex flex-wrap gap-3">
            {flows.map((f) => (
              <Link key={f.id} to={`/guided/${f.id}`} data-testid={`guided-flow-${f.id}`}>
                <Button variant="outline" className="gap-2 border-mlily-blue/40 text-mlily-blue hover:bg-mlily-blue hover:text-white">
                  {f.category} <span className="opacity-60">→ start</span>
                </Button>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="article-results">
        {results.length === 0 && <p className="text-slate-400">No matching articles.</p>}
        {results.map((a) => (
          <Card key={a.id} onClick={() => setSelected(a)} data-testid={`article-${a.id}`}
            className="p-5 cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all duration-300 border hover:border-amber-500">
            <div className="flex flex-wrap gap-2 mb-2">
              <Classification value={a.classification} />
              <Badge className="bg-amber-500">{a.product_category}</Badge>
              {a.model && <Badge variant="outline">{a.model}</Badge>}
            </div>
            <h3 className="font-heading font-bold text-slate-900">{a.problem}</h3>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{a.symptoms}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
